import React from 'react';
import { makeStyles, useTheme } from "@mui/styles";
import { Box, Skeleton, Typography } from '@mui/material';
import Chip from 'components/Chip';
import HorizontalScrollNavigator from 'components/HorizontalScrollNavigator';
import { NoResultComponent } from './TodoList';
import { useEffect } from 'react';
import { getMyMockList } from 'services';
import { useState } from 'react';
import { useServices, useSnackbar } from 'contexts';
import dayjs from 'dayjs';
import StartAssessmentDialog from 'dialogs/StartAssessmentDialog';
import { useSearchParams } from 'react-router-dom';
import { FilterChips } from 'components/Filter';
import { filters } from './PersonalizedInterviewList';
import { useNavigateWithClient } from 'hooks';
import NoAccessTooltip from 'components/NoAccessTooltip';

const useStyles = makeStyles(theme => ({
  cardContainer: {
    position: "relative",
    padding: theme.spacing(5),
    display: "flex",
    flexDirection: "column",
    justifyContent: 'space-between',
    width: "405px",
    height: "210px",
    border: `3px solid ${theme.palette.neutral['clr-200']}`,
    borderRadius: theme.spacing(2),
    cursor: "pointer",
    flex: "0 0 auto",
    transition: "box-shadow 10ms linear",
    '&:hover': {
      border: `3px solid ${theme.palette.primary['clr-100']}`,
      transition: 'box-shadow 0.1s ease-in-out',
    },
    overflow: 'hidden',
    backgroundColor: theme.palette.shades['clr-white-900']
  },
  disabledCard: {
    position: 'relative',
    padding: theme.spacing(5),
    display: "flex",
    flexDirection: "column",
    justifyContent: 'space-between',
    width: "405px",
    height: "210px",
    border: `3px solid ${theme.palette.neutral['clr-300']}`,
    borderRadius: theme.spacing(2),
    backgroundColor: theme.palette.shades['clr-grey-100'],
    cursor: 'default',
    flex: "0 0 auto",
    transition: "box-shadow 10ms linear",
    overflow: 'hidden',
    '& > *': {
      color: `${theme.palette.neutral['clr-600']} !important`
    },
    '&:hover': {
      border: `3px solid ${theme.palette.neutral['clr-300']}`,
    },
  },
  header: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    zIndex: 2
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    zIndex: 2
  },
  truncate: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  attempt: {
    ...theme.typography['body01-semiBold'],
    color: theme.palette.neutral['clr-700'],
    display: "flex",
    alignItems: "center"
  },
}));


const ContestCard = ({ template, onExpire, disabled = false }) => {
  const classes = useStyles();
  const theme = useTheme();
  const snackbar = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();

  const [timeRemaining, setTimeRemaining] = useState('');
  const [status, setStatus] = useState('Upcoming');
  const [startInterview, setStartInterview] = useState(false);
  const [isNeverExpiring, setIsNeverExpiring] = useState(false);

  useEffect(() => {
    if (!template) return;

    const publishDate = dayjs(template.settings.publishOn);
    const expiryDate = dayjs(template.settings.expiresOn);

    const isNever = expiryDate.year() === 9999;
    setIsNeverExpiring(isNever);

    const updateStatusAndTimer = () => {
      const now = dayjs();

      if (isNever) {
        setStatus('Live');
        setTimeRemaining('');
        return;
      }

      let newStatus;
      let timeText = '';

      if (now.isAfter(expiryDate)) {
        newStatus = 'Ended';
        if (status === 'Live' && onExpire) {
          onExpire(template._id);
        }
      } else if (now.isAfter(publishDate) || now.isSame(publishDate)) {
        newStatus = 'Live';
        const timeDiff = expiryDate.diff(now, 'second');
        if (timeDiff > 0) {
          if (timeDiff > 86400) { // more than 24 hours
            const days = Math.floor(timeDiff / 86400);
            const hours = Math.floor((timeDiff % 86400) / 3600);
            timeText = `${days} Day${days > 1 ? 's' : ''} ${hours} Hr${hours > 1 ? 's' : ''}`;
          } else {
            const hours = Math.floor(timeDiff / 3600);
            const minutes = Math.floor((timeDiff % 3600) / 60);
            const seconds = timeDiff % 60;
            timeText = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
          }
        }
      } else {
        newStatus = 'Upcoming';
        const timeDiff = publishDate.diff(now, 'second');
        if (timeDiff > 0) {
          if (timeDiff > 86400) { // more than 24 hours
            const days = Math.floor(timeDiff / 86400);
            const hours = Math.floor((timeDiff % 86400) / 3600);
            timeText = `${days} Day${days > 1 ? 's' : ''} ${hours} Hr${hours > 1 ? 's' : ''}`;
          } else {
            const hours = Math.floor(timeDiff / 3600);
            const minutes = Math.floor((timeDiff % 3600) / 60);
            const seconds = timeDiff % 60;
            timeText = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
          }
        }
      }

      setStatus(newStatus);
      setTimeRemaining(timeText);
    };

    updateStatusAndTimer();

    const interval = setInterval(updateStatusAndTimer, 1000);

    return () => clearInterval(interval);
  }, [template, status, onExpire]);

  const isEnded = status === 'Ended';
  const isLive = status === 'Live';

  const chipColor = {
    Upcoming: theme.palette.tertiary['clr-700'],
    Live: '#009970',
    Ended: '#E2483C',
  }[status];

  const chipText = status;
  const cardClass = (isEnded || disabled) ? classes.disabledCard : classes.cardContainer;

  const formatDate = (dateString) => dayjs(dateString).format('DD MMM YYYY | hh:mmA');

  const startContest = () => {
    if (template?.totalAttempts >= template?.settings?.maxAllowedAttempts) {
      snackbar.error("You cannot retake this contest.");
    } else if (dayjs(template?.settings?.publishOn).isAfter(dayjs())) {
      snackbar.error("You can attempt once it goes live.");
    } else if (dayjs(template?.settings?.expiresOn).isBefore(dayjs())) {
      snackbar.error("The contest has ended.");
    } else {
      setStartInterview(true);
    }
  };

  useEffect(() => {
    const cid = searchParams.get('cid');
    if (!cid || !template._id || disabled) return;
    if (cid === template._id) {
      setStartInterview(true);
    }
  }, [searchParams, template._id, disabled]);

  return (
    <>
      <NoAccessTooltip show={disabled}>
      <Box className={cardClass} onClick={disabled ? undefined : startContest}>
        <img
          src={
            isEnded
              ? 'https://assets.languify.in/images/contestCardDisabled.png'
              : 'https://assets.languify.in/images/contestCardBg.png'
          }
          alt='bg'
          style={{ position: 'absolute', right: 0, top: 0 }}
          height={208}
        />
        <Box className={classes.header}>
          {isNeverExpiring ? (
            <Chip
              content="Never Expires"
              bgColor={theme.palette.primary['clr-50']}
              color={theme.palette.primary['clr-800']}
              sx={{ borderRadius: '4px', padding: '4px 8px' }}
            />
          ) : (
            <Chip
              content={chipText}
              bgColor={chipColor}
              color={theme.palette.shades['clr-white-900']}
              sx={{ borderRadius: '4px', padding: '4px 8px' }}
            />
          )}
          {!isEnded && !isNeverExpiring && timeRemaining && (
            <Typography
              variant='body01-bold'
              style={{
                color: isLive ? '#C51407' : theme.palette.tertiary['clr-700']
              }}
            >
              {isLive ? `Ending in: ${timeRemaining}` : `Starting in: ${timeRemaining}`}
            </Typography>
          )}
        </Box>
        <Box className={classes.content}>
          <Typography
            variant='h3-semiBold'
            color='neutral.clr-700'
            className={classes.truncate}
          >
            {template.settings.name}
          </Typography>
          <Box display='flex' flexDirection='column' gap='4px'>
            <Box>
              <Typography variant='body01-bold'>Started on: </Typography>
              <Typography variant='body01-bold' color='primary.main'>
                {formatDate(template.settings.publishOn)}
              </Typography>
            </Box>
            <Box>
              <Typography variant='body01-bold'>Available time: </Typography>
              <Typography variant='body01-bold' color='primary.main'>
                60 min
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
      </NoAccessTooltip>

      <StartAssessmentDialog
        onClose={() => setStartInterview(false)}
        open={startInterview}
        template={{
          _id: template?.details?.challenges[0],
          name: template?.settings?.name
        }}
        codingInterview={true}
        stateParams={{
          data: 'contest',
          tagId: template?._id
        }}
      />
    </>
  );
};

export const MockCard = ({ template, disabled = false }) => {
  const classes = useStyles();
  const theme = useTheme();
  const snackbar = useSnackbar();
  const navigateWithClient = useNavigateWithClient();

  const [startInterview, setStartInterview] = useState(false);

  const isNew = (() => {
    if (!template?.createdAt) return false;
    const createdTime = new Date(template.createdAt).getTime();
    return Date.now() - createdTime < 24 * 60 * 60 * 1000;
  })();

  const expiryChip = (() => {
    const expiresOn = template?.settings?.expiresOn;
    if (!expiresOn) return null;

    const expiryDate = new Date(expiresOn);
    const isNever =
      expiryDate.getFullYear() === 9999 &&
      expiryDate.getMonth() === 11 &&
      expiryDate.getDate() === 31;

    if (isNever) {
      return (
        <Chip
          content="Never Expires"
          bgColor={theme.palette.primary['clr-50']}
          color={theme.palette.primary['clr-800']}
          sx={{ borderRadius: '4px', padding: '4px 8px' }}
        />
      );
    } else {
      const day = String(expiryDate.getDate()).padStart(2, '0');
      const month = String(expiryDate.getMonth() + 1).padStart(2, '0');
      const year = expiryDate.getFullYear();
      return (
        <Chip
          content={`Expiry: ${day}/${month}/${year}`}
          bgColor={theme.palette.primary['clr-50']}
          color={theme.palette.primary['clr-800']}
          sx={{ borderRadius: '4px', padding: '4px 8px' }}
        />
      );
    }
  })();

  const handleFeedbackClick = () => {
    navigateWithClient(`/performance/4c/${template?.latestAttempt._id}`)
  };

  const handleCardClick = () => {
    if (dayjs(template?.settings?.expiresOn).isBefore(dayjs())) {
      snackbar.error("This mock has expired!");
    } else if (template?.totalAttempts < template?.settings?.maxAllowedAttempts) {
      setStartInterview(true);
    } else {
      snackbar.error("You’ve hit the maximum attempt limit!");
    }
  }

  return (<>
    <NoAccessTooltip show={disabled}>
    <Box className={disabled ? classes.disabledCard : classes.cardContainer} onClick={disabled ? undefined : () => handleCardClick()}>
      <Box display="flex" justifyContent="space-between">
        <Box display="flex" flexWrap="wrap" gap="8px">
          {isNew && (
            <Chip
              content="New"
              bgColor={theme.palette.tertiary['clr-50']}
              color={theme.palette.tertiary['clr-800']}
              sx={{ borderRadius: '4px', padding: '4px 8px' }}
            />
          )}
          {expiryChip}
        </Box>

        {template.totalAttempts > 0 && (
          <Box
            sx={{ cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              handleFeedbackClick();
            }}
          >
            <Chip
              content="View feedback"
              bgColor={theme.palette.primary.main}
              color={theme.palette.shades['clr-white-900']}
              typographyVariant="body01-link"
              sx={{ borderRadius: '4px', padding: '4px 8px' }}
            />
          </Box>
        )}
      </Box>

      <Box display="flex" flexDirection="column" gap="8px">
        <Box display="flex" flexDirection="column" gap="4px">
          <Typography variant="body02-bold" color="neutral.clr-600">
            Coding Mock
          </Typography>
          <Typography
            variant="h3-semiBold"
            color="neutral.clr-800"
            className={classes.truncate}
          >
            {template?.settings?.name}
          </Typography>
        </Box>

        <Typography className={classes.attempt}>
          <img src="https://assets.languify.in/images/refreshIcon.svg" alt="refresh" />&nbsp;
          {template.totalAttempts > 0
            ? `${template.totalAttempts} attempt${template.totalAttempts > 1 ? 's' : ''}`
            : 'No attempts'}
        </Typography>
      </Box>

    </Box>
    </NoAccessTooltip>
    <StartAssessmentDialog
      onClose={() => setStartInterview(false)}
      open={startInterview}
      template={{ _id: template?.details?.challenges[0], name: template?.settings?.name }}
      codingInterview={true}
      stateParams={{
        data: 'mock',
        tagId: template?._id
      }}
    />
  </>
  );
};

function TemplateCardSkeleton() {
  const classes = useStyles();

  return (
    <Box className={classes.cardContainer}>
      <Box display="flex">
        <Skeleton variant="rectangular" width="20%" height='30px' style={{ borderRadius: '20px' }} />
      </Box>
      <Box className={classes.cardContent}>
        <Box>
          <Skeleton variant="rectangular" width="90%" height='40px' />
        </Box>
        <Box mt='10px'>
          <Skeleton variant="rectangular" width="100%" height='20px' />
        </Box>
      </Box>
    </Box>
  );
}

function List({
  templates = [], loader, disabled = false
}) {

  return (
    <>
      {loader
        ? Array.from(new Array(3)).map((_, i) => (
          <TemplateCardSkeleton key={i} />
        ))
        : templates.map((template, index) => {
          if (template.type === 'mock') {
            return <MockCard key={index} template={template} disabled={disabled} />;
          } else {
            return <ContestCard key={index} template={template} disabled={disabled} />;
          }
        })}
    </>
  );
}


const ContestCardList = ({
  searchQuery = "", setSearchQuery = () => { }, mocks = [], disabled = false
}) => {
  const classes = useStyles();

  const [loader, setLoader] = useState(false);
  const [filteredMock, setFilteredMock] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState({});

  useEffect(() => {
    const isFilterApplied = Object.keys(appliedFilters).length > 0;

    const filtered = mocks.filter((mock) => {
      const nameToSearch = mock.settings.name;

      return (
        (!isFilterApplied ||
          (((dayjs(mock.settings?.expiresOn).diff(dayjs(), 'hour') < 24) && appliedFilters.expiresSoon) ||
            ((dayjs(mock.settings?.publishOn).diff(dayjs(), 'hour') < 24) && appliedFilters.new))) &&
        (searchQuery === '' || nameToSearch.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    });

    setFilteredMock(filtered);
  }, [mocks, appliedFilters, searchQuery]);

  const handleFilterChange = ({ field }) => {
    setAppliedFilters(prevFilters => {
      const newFilters = { ...prevFilters };

      if (newFilters[field]) {
        delete newFilters[field];
      } else {
        newFilters[field] = true;
      }

      return newFilters;
    });
    setSearchQuery("");
  };

  return !mocks.length ? null : (
    <Box className={classes.container}>
      <FilterChips
        title="Filter by:"
        filters={filters}
        appliedFilters={appliedFilters}
        onChange={handleFilterChange}
      />
      <Box height={280} mt={5}>
        {filteredMock.length || loader ? (
          <HorizontalScrollNavigator
            NoResultComponent={NoResultComponent}
            NoResultComponentProps={"No mock found for the selected filter!"}
            childrensLength={filteredMock.length}
            scrollWidth={1400}
          >
            <List
              templates={filteredMock}
              loader={loader}
              disabled={disabled}
            />
          </HorizontalScrollNavigator>
        ) : (
          <NoResultComponent
            description="Nothing here yet! You can attempt mock once it is published for your batch."
          />
        )}
      </Box>
    </Box>
  )
}

export default ContestCardList
